// SPDX-FileCopyrightText: 2024 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { nativeVerticalScrollStyles } from 'components/common/scroll/nativeScrollStyles';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useUserContractsApi from 'hooks/useUserContractsApi';
import styled from 'styled-components';
import { User, UserType } from 'model/User';
import useRolesApi from 'hooks/useRolesApi';
import ContractsTable from '../Contracts/ContractsTable';
import { Checkbox, Footer, FooterButton, NotificationBanner, InputFieldText } from '@tls/treact-ui';
import { IconLoading } from '@tls/treact-ui/icons';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: calc(100% - 1px + 1px);
`;

const Form = styled.form`
  flex-grow: 1;
  ${nativeVerticalScrollStyles};
`;

const Grid = styled.div`
  display: grid;
  padding: 1rem;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 1rem;
`;
const GridItem = styled.div``;

enum EditAction {
  Save,
  Delete,
}
export default function UserDetails({
  user,
  users,
  addOrEditUser,
  deleteUser,
  onClose,
  isUsernameEditable = true,
  isLoading = false,
  showContracts = true,
}: Readonly<Props>) {
  const { t } = useTranslation();
  const { roles } = useRolesApi();
  const { data: contracts, isLoading: isContractsLoading } = useUserContractsApi(user.id ?? '');

  const [editUser, setEditUser] = useState(user);
  const [action, setAction] = useState<EditAction>();
  const isMyTrumpfUser = useMemo(() => user.type === UserType.MyTrumpf, [user.type]);

  const isNameAlreadyExist = useMemo(
    () =>
      (users?.filter(otherUser => otherUser.id !== user.id && otherUser.userName === editUser.userName)?.length ?? 0) >
      0,
    [editUser, user, users]
  );

  const onDelete = useCallback(async () => {
    setAction(EditAction.Delete);
    if (deleteUser) await deleteUser(user);
    setAction(undefined);
    if (onClose) onClose();
  }, [deleteUser, onClose, user]);

  const onAccept = useCallback(async () => {
    setAction(EditAction.Save);
    await addOrEditUser(editUser);
    setAction(undefined);
    if (onClose) onClose();
  }, [editUser, onClose, addOrEditUser]);

  useEffect(() => {
    setEditUser(user);
  }, [user]);

  const toggleRoles = useCallback(
    (newRole: string) => {
      if (editUser?.roles?.includes(newRole)) {
        setEditUser({
          ...editUser,
          roles: editUser.roles?.filter(role => role !== newRole) ?? [],
        });
      } else if (editUser) {
        setEditUser({
          ...editUser,
          roles: [...(editUser.roles ?? []), newRole],
        });
      }
    },
    [editUser, setEditUser]
  );

  const hasUserChanges = useMemo(() => JSON.stringify(editUser) !== JSON.stringify(user), [editUser, user]);

  const editing = action === EditAction.Save;

  const rightActions: React.JSX.Element[] = [];
  if (onClose) {
    rightActions.push(
      <FooterButton
        key='abort'
        variant={'secondary'}
        label={t('cancel')}
        onClick={onClose}
        disabled={isLoading}
        data-testid='settings-user-cancel-button'
      />
    );
  }
  rightActions.push(
    <FooterButton
      key='save'
      variant={'primary'}
      label={t('save')}
      onClick={onAccept}
      icon={editing ? <IconLoading /> : undefined}
      disabled={isLoading || !hasUserChanges || editing}
      data-testid='settings-user-save-button'
    />
  );

  if (editUser.id !== user.id) return <></>;

  const deleting = action === EditAction.Delete;

  return (
    <Root>
      <Form autoComplete='off'>
        <Grid>
          <GridItem>
            <InputFieldText
              label={t('user.userName')}
              value={editUser.userName}
              type={isNameAlreadyExist ? 'error' : 'none'}
              helperText={isNameAlreadyExist ? t('user.validation.userNameError') : undefined}
              data-testid='settings-user-username-input'
              onValueChange={newValue =>
                setEditUser({
                  ...editUser,
                  userName: newValue,
                })
              }
              disabled={!isUsernameEditable}
            />
            <InputFieldText
              label={t('user.email')}
              value={editUser.email ?? ''}
              data-testid='settings-user-email-input'
              onValueChange={newValue =>
                setEditUser({
                  ...editUser,
                  email: newValue,
                })
              }
            />
          </GridItem>
          <GridItem>
            <InputFieldText
              label={t('user.lastName')}
              value={editUser.lastName ?? ''}
              data-testid='settings-user-last-name-input'
              onValueChange={newValue =>
                setEditUser({
                  ...editUser,
                  lastName: newValue,
                })
              }
            />

            <InputFieldText
              label={t('user.firstName')}
              value={editUser.firstName ?? ''}
              data-testid='settings-user-first-name-input'
              onValueChange={newValue =>
                setEditUser({
                  ...editUser,
                  firstName: newValue,
                })
              }
            />
          </GridItem>

          {!isMyTrumpfUser && (
            <GridItem>
              <span className='font-size_m font-weight_bold title'>{t('settings.roles.title')}</span>
              {roles?.map(r => (
                <LeftAlignedCheckbox
                  key={r.id}
                  label={r.name}
                  value={
                    editUser.roles?.includes(r.id ?? '') ? Checkbox.Selection.selected : Checkbox.Selection.unselected
                  }
                  onValueChange={() => toggleRoles(r.id ?? '')}
                  data-testid={`settings-user-role-${r.id ?? 'unknown'}-checkbox`}
                />
              ))}
            </GridItem>
          )}
        </Grid>
        {isMyTrumpfUser && (
          <NotificationBanner
            kind='info'
            title={t('settings.roles.myTrumpfUserInfo.title')}
            description={t('settings.roles.myTrumpfUserInfo.description')}
            data-testid='settings-user-mytrumpf-info'
          />
        )}
        {showContracts ? <ContractsTable data={contracts} isLoading={isContractsLoading} /> : null}
      </Form>
      <Footer
        left={
          deleteUser
            ? [
                <FooterButton
                  key='delete'
                  variant={'secondary'}
                  label={t('delete')}
                  onClick={onDelete}
                  icon={deleting ? <IconLoading /> : undefined}
                  disabled={isLoading || deleting}
                  data-testid='settings-user-delete-button'
                />,
              ]
            : []
        }
        right={rightActions}
      />
    </Root>
  );
}

interface Props {
  user: User;
  users?: User[];
  onClose?: () => void;
  addOrEditUser: (user: User) => Promise<unknown>;
  deleteUser?: (user: User) => Promise<unknown>;
  isUsernameEditable?: boolean;
  isLoading?: boolean;
  showContracts?: boolean;
}

const LeftAlignedCheckbox = styled(Checkbox)`
  align-items: start;
`;
